/* eslint-disable baseline-js/use-baseline -- touch/wheel events drive intentional pull-to-refresh gestures and are supported by all target browsers */
import { SuccessIcon, ThemeColorVariable, ThemeSpaceVariable } from "@caspeco/casper-ui-library";
import React, { useEffect, useRef, useState } from "react";
import Spinner from "./spinner";
import { useAppContext } from "../hooks/use-app-context";
interface ReloadContainerProps {
	children: React.ReactNode;
	loadingState?: boolean;
	reload?: () => void;
	onScrollBorttom?: (event: React.UIEvent<HTMLDivElement>) => void;
	isTimeReport?: boolean;
	// scrollRef: React.RefObject<HTMLDivElement>;
}

const ReloadContainer = ({
	children,
	loadingState,
	reload,
	onScrollBorttom,
	// scrollRef,
}: ReloadContainerProps) => {
	const [height, setHeight] = useState<number>(0);
	const [isAnimating, setIsAnimating] = useState<boolean>(false);
	const scrollableDivRef = useRef<HTMLDivElement>(null);
	const [isLoading, setIsLoading] = useState(loadingState);
	const [isLoaded, setIsLoaded] = useState(false);
	const [fadeOut, setFadeout] = useState(false);
	const { safeArea } = useAppContext();

	useEffect(() => {
		const scrollableDiv = scrollableDivRef.current;
		if (!scrollableDiv || !reload) return;
		let startY = 0;
		let atTopOfElement = false;
		let lockEvent = false;

		const handleTouchStart = (event: TouchEvent) => {
			startY = event.touches[0].clientY;
			atTopOfElement = scrollableDiv.scrollTop === 0;
		};

		const handleTouchMove = (event: TouchEvent) => {
			if (isLoading || lockEvent) return;

			if (!atTopOfElement) return;
			const touchY = event.touches[0].clientY;
			const delta = touchY - startY;

			if (delta > 200 + safeArea.top) {
				lockEvent = true;
				setIsAnimating(true);
				setHeight(0);
				setIsLoading(true);
				reload();
			} else if (delta > 0) {
				setIsAnimating(false);
				setHeight(delta / 2 + safeArea.top);
			}
		};

		const handleTouchEnd = (event: TouchEvent) => {
			const touchY = event.changedTouches[0].clientY;
			const delta = touchY - startY;

			if (!isLoading && atTopOfElement && delta > 0) {
				setIsAnimating(true);
				atTopOfElement = false;
				setHeight(0);
				setTimeout(() => setIsAnimating(false), 500);
			}
		};

		const handleWheel = (event: WheelEvent) => {
			scrollableDiv.scrollBy({ top: event.deltaY });
		};

		scrollableDiv.addEventListener("touchstart", handleTouchStart);
		scrollableDiv.addEventListener("touchmove", handleTouchMove);
		scrollableDiv.addEventListener("touchend", handleTouchEnd);
		scrollableDiv.addEventListener("wheel", handleWheel);
		return () => {
			scrollableDiv.removeEventListener("touchstart", handleTouchStart);
			scrollableDiv.removeEventListener("touchmove", handleTouchMove);
			scrollableDiv.removeEventListener("touchend", handleTouchEnd);
			scrollableDiv.removeEventListener("wheel", handleWheel);
		};
	}, [reload, isLoading, safeArea.top]);

	useEffect(() => {
		if (!loadingState && isLoading) {
			setIsLoaded(true);
			setTimeout(() => {
				setIsLoaded(false);
				setIsLoading(false);
				setFadeout(false);
			}, 1500);
			setTimeout(() => {
				setFadeout(true);
			}, 1000);
		}
	}, [loadingState, isLoading]);

	if (!reload) {
		return children;
	}
	const animationStyles = {
		transition: isAnimating ? "margin-top 0.5s ease" : "none",
	};

	const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
		const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;

		if (onScrollBorttom && Math.ceil(scrollTop) + clientHeight >= scrollHeight) {
			onScrollBorttom(event);
		}
	};

	return (
		<div
			ref={scrollableDivRef}
			style={{
				overflowY: "scroll",
				overflowX: "hidden",
				height: "100%",
			}}
			onScroll={handleScroll}
		>
			{(isLoading || height > 0 || isAnimating) && (
				<div
					style={{
						width: "100%",
						position: "absolute",
						display: "flex",
						justifyContent: "center",
						top: "-50px",
						left: 0,
					}}
				>
					<div
						style={{
							...animationStyles,
							marginTop:
								isLoading && !fadeOut
									? `calc(${ThemeSpaceVariable.Medium} + 50px + ${safeArea.top}px)`
									: height,
							backgroundColor: isLoaded
								? ThemeColorVariable.SuccessContainer
								: ThemeColorVariable.PrimaryContainer,
							padding: ThemeSpaceVariable.Small,
							borderRadius: "100%",
							zIndex: 5,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						{isLoaded ? (
							<SuccessIcon fontSize={ThemeSpaceVariable.Large} />
						) : (
							<Spinner
								transform={`rotate(${height * 2.5}deg)`}
								transition={isAnimating ? "transform 0.5s ease" : "none"}
								animationDuration={isLoading ? "0.42s" : "0s"}
								color={ThemeColorVariable.Accent}
							/>
						)}
					</div>
				</div>
			)}
			{children}
		</div>
	);
};

export default ReloadContainer;
